import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Container, Button, Modal, Form, Row, Col } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import { toast, ToastContainer } from 'react-toastify';
import axios from 'axios';
import GLOBALS from '../../utils/Globals';
import 'react-toastify/dist/ReactToastify.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';

const FieldListManager = () => {
  // State for field lists data
  const [fieldLists, setFieldLists] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [currentFieldList, setCurrentFieldList] = useState(null);
  
  // State for delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFieldList, setDeleteFieldList] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    description: ''
  });
  
  // Grid reference
  const gridRef = useRef();
  
  // Column definitions for AG Grid
  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Field Name',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'value',
      headerName: 'Value',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      filter: true,
      flex: 2
    },
    {
      headerName: 'Actions',
      flex: 1,
      cellRenderer: (params) => (
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(params.data)}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteClick(params.data)}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      ),
      sortable: false,
      filter: false
    }
  ], []);
  
  // Default column definitions
  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true
  }), []);
  
  // Grid options
  const gridOptions = useMemo(() => ({
    rowSelection: 'multiple',
    pagination: true,
    paginationPageSize: 10
  }), []);
  
  // Fetch field lists on component mount
  useEffect(() => {
    fetchFieldLists();
  }, []);
  
  // Function to fetch field lists from API
  const fetchFieldLists = async () => {
    setLoading(true);
    try {
      // This will be replaced with actual API call
      // const baseURL = process.env.REACT_APP_ENV === "PROD"
      //   ? GLOBALS.PROD.baseURLGetFieldLists
      //   : GLOBALS.DEV.baseURLGetFieldLists;
      // const response = await axios.get(baseURL);
      // setFieldLists(response.data);
      
      // Mock data for now
      setFieldLists([
        { id: '1', name: 'Product Name', value: 'Default Product', description: 'The name of the product' },
        { id: '2', name: 'SKU', value: 'SKU-12345', description: 'Stock Keeping Unit identifier' },
        { id: '3', name: 'Category', value: 'Electronics', description: 'Product category' }
      ]);
    } catch (error) {
      console.error('Error fetching field lists:', error);
      toast.error('Failed to load field lists');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle opening the add modal
  const handleAddNew = () => {
    setModalMode('add');
    setFormData({
      name: '',
      value: '',
      description: ''
    });
    setShowModal(true);
  };
  
  // Handle opening the edit modal
  const handleEdit = (fieldList) => {
    setModalMode('edit');
    setCurrentFieldList(fieldList);
    setFormData({
      name: fieldList.name,
      value: fieldList.value,
      description: fieldList.description
    });
    setShowModal(true);
  };
  
  // Handle opening delete modal
  const handleDeleteClick = (fieldList) => {
    setDeleteFieldList(fieldList);
    setShowDeleteModal(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = () => {
    if (deleteFieldList) {
      deleteField(deleteFieldList.id);
      setShowDeleteModal(false);
      setDeleteFieldList(null);
    }
  };
  
  // Function to delete field list
  const deleteField = async (id) => {
    try {
      // This will be replaced with actual API call
      // const baseURL = process.env.REACT_APP_ENV === "PROD"
      //   ? GLOBALS.PROD.baseURLDeleteFieldLists
      //   : GLOBALS.DEV.baseURLDeleteFieldLists;
      // await axios.delete(`${baseURL}/${id}`);
      
      // Mock deletion for now
      setFieldLists(fieldLists.filter(item => item.id !== id));
      toast.success('Field list deleted successfully');
    } catch (error) {
      console.error('Error deleting field list:', error);
      toast.error('Failed to delete field list');
    }
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.value) {
      toast.error('Name and Value are required fields');
      return;
    }
    
    try {
      if (modalMode === 'add') {
        // This will be replaced with actual API call
        // const baseURL = process.env.REACT_APP_ENV === "PROD"
        //   ? GLOBALS.PROD.baseURLSaveFieldLists
        //   : GLOBALS.DEV.baseURLSaveFieldLists;
        // const response = await axios.post(baseURL, formData);
        
        // Mock adding for now
        const newFieldList = {
          id: Date.now().toString(),
          ...formData
        };
        setFieldLists([...fieldLists, newFieldList]);
        toast.success('Field list added successfully');
      } else {
        // This will be replaced with actual API call
        // const baseURL = process.env.REACT_APP_ENV === "PROD"
        //   ? GLOBALS.PROD.baseURLUpdateFieldLists
        //   : GLOBALS.DEV.baseURLUpdateFieldLists;
        // await axios.put(`${baseURL}/${currentFieldList.id}`, formData);
        
        // Mock updating for now
        setFieldLists(fieldLists.map(item => 
          item.id === currentFieldList.id ? { ...item, ...formData } : item
        ));
        toast.success('Field list updated successfully');
      }
      
      setShowModal(false);
    } catch (error) {
      console.error('Error saving field list:', error);
      toast.error('Failed to save field list');
    }
  };
  
  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Field Lists Manager</h2>
        <Button variant="outline-primary" onClick={handleAddNew}>
          Add New Field
        </Button>
      </div>
      
      <div className="ag-theme-material" style={{ height: '600px', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={fieldLists}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          animateRows={true}
          pagination={true}
          paginationPageSize={10}
        />
      </div>
      
      {/* Modal for Add/Edit Field List */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            {modalMode === 'add' ? (
              <><i className="bi bi-plus-circle me-2"></i>Add New Field</>
            ) : (
              <><i className="bi bi-pencil-square me-2"></i>Edit Field</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 py-4">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label>
                <strong>Field Name</strong>
                <span className="text-danger ms-1">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter field name"
                required
                className="shadow-sm"
              />
              <Form.Text className="text-muted">
                A unique name to identify this field
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>
                <strong>Value</strong>
                <span className="text-danger ms-1">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="value"
                value={formData.value}
                onChange={handleInputChange}
                placeholder="Enter field value"
                required
                className="shadow-sm"
              />
              <Form.Text className="text-muted">
                The default value for this field
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label><strong>Description</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter field description"
                className="shadow-sm"
              />
              <Form.Text className="text-muted">
                Optional description to explain the purpose of this field
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowModal(false)}
                className="px-4"
              >
                Cancel
              </Button>
              <Button 
                variant="outline-primary" 
                type="submit"
                className="px-4"
              >
                {modalMode === 'add' ? 'Add Field' : 'Update Field'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => {
          setShowDeleteModal(false);
          setDeleteFieldList(null);
        }}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center">
            <div className="text-danger me-3">
              <i className="bi bi-exclamation-triangle-fill fs-1"></i>
            </div>
            <div>
              <h5 className="mb-2">Delete Field</h5>
              <p className="mb-0">
                Are you sure you want to delete the field "{deleteFieldList?.name}"? 
                This action cannot be undone.
              </p>
              {deleteFieldList?.description && (
                <p className="mb-0 text-muted">
                  <small>Description: {deleteFieldList.description}</small>
                </p>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="outline-secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteFieldList(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="outline-danger" 
            onClick={handleDeleteConfirm}
          >
            Delete Field
          </Button>
        </Modal.Footer>
      </Modal>
      
      <ToastContainer position="bottom-right" />
    </Container>
  );
};

export default FieldListManager;
